import { Camera, Image, Share2 } from 'lucide-react';
import { Label } from '~/components/ui/label';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import { Separator } from '~/components/ui/separator';
import { SignatureField } from '~/components/forms/SignatureField';

/**
 * Media Release Step for a single student
 *
 * - Consent level: FULL, LIMITED, or NONE
 * - Typed signature
 */
export function MediaReleaseStep({
  student,
  release,
  onReleaseChange
}: any) {
  const { firstName, lastName } = student;
  const studentName = `${firstName} ${lastName}`;

  const handleConsentChange = (value: string) => {
    onReleaseChange({
      consent: value
    });
  };

  const handleSignatureChange = (sig: string) => {
    onReleaseChange({
      signature: sig,
      signedAt: sig ? new Date().toISOString() : null
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 mb-3">
          <Camera className="h-6 w-6 text-blue-600" />
        </div>
        <h2 className="text-xl font-semibold">Media Release</h2>
        <p className="text-muted-foreground">For {studentName}</p>
      </div>

      <div className="bg-slate-50 p-4 rounded-lg space-y-2 text-sm">
        <p>
          Throughout the school year, we may take photographs and videos of students
          participating in school activities, events, and daily learning. These images
          may be used for:
        </p>
        <ul className="list-disc ml-6 space-y-1">
          <li>School website and social media</li>
          <li>Marketing materials and brochures</li>
          <li>Newsletters and annual reports</li>
          <li>Local media coverage of school events</li>
        </ul>
      </div>

      <div className="space-y-4">
        <Label className="text-base font-medium">Photo/Video Release Authorization</Label>

        <RadioGroup
          value={release.consent || undefined}
          onValueChange={handleConsentChange}
          className="space-y-4"
        >
          <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-slate-50 cursor-pointer">
            <RadioGroupItem value="FULL" id="media-full" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="media-full" className="font-medium cursor-pointer flex items-center gap-2">
                <Share2 className="h-4 w-4 text-green-600" />
                Full Consent
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                I grant permission for my child's photo/video to be used in all school
                publications, website, social media, and marketing materials.
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-slate-50 cursor-pointer">
            <RadioGroupItem value="LIMITED" id="media-limited" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="media-limited" className="font-medium cursor-pointer flex items-center gap-2">
                <Image className="h-4 w-4 text-amber-600" />
                Limited Consent
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                I grant permission for internal use only (newsletters to school families,
                classroom displays). No public-facing or marketing use.
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-slate-50 cursor-pointer">
            <RadioGroupItem value="NONE" id="media-none" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="media-none" className="font-medium cursor-pointer flex items-center gap-2">
                <Camera className="h-4 w-4 text-red-600 line-through" />
                No Consent
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                I do not grant permission for my child to be photographed or recorded
                for any purpose beyond required school documentation.
              </p>
            </div>
          </div>
        </RadioGroup>
      </div>

      <Separator />

      <SignatureField
        value={release.signature || ''}
        onChange={handleSignatureChange}
        label="Electronic Signature"
        description={`By typing your name below, you confirm your media release selection for ${studentName}.`}
        id="media-signature"
        asCard={false}
      />
    </div>
  );
}

export default MediaReleaseStep;
